import { Stack } from '@landler/component-library';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { SpeciesEnum } from '@/api/rest/resources/types/fact';
import { INDICATOR } from '@/api/rest/resources/types/IndicatorProduct';
import { AccordionSection, AccordionType, AccordionWrapper } from '@/components/widgets/o/Accordion/Accordion';
import { useSelectedIndicatorProducts } from '@/pages/shared/indicators/hooks/useIndicatorProducts';
import { AboveGroundCarbon } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components//aboveGroundCarbon/AboveGroundCarbonCard';
import { BelowGroundCarbon } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/carbon/BelowGroundCarbonCard';
import { Deforestation } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/deforestation/DeforestationCard';
import { EcosystemIntegrityIndex } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/eii/EiiCard';
import { HabitatIntactness } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/habitatIntactness/HabitatIntactnessCard';
import { HabitatIntactnessThreatsBreakdown } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/habitatThreatsBreakdown/HabitatIntactnessThreatsBreakdownCard';
import { HabitatIntactnessThreatsTable } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/habitatThreatsTable/HabitatIntactnessThreatsTableCard';
import { LandCoverChangeCard } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/landCover/LandCoverChangeCard';
import { LandSurfaceTemperature } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/lst/LstCard';
import { NDVI } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/ndvi/NDVICard';
import { PopulationDensity } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/populationDensity/PopulationDensityCard';
import {
  BiodiversityUpliftCard,
  ProjectProgressCard,
  SurvivalRateCard,
} from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/speciesGradient/OverviewStatsTile';
import { SpeciesGradient } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/speciesGradient/SpeciesGradientCard';
import { ThreatenedSpecies } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/threatenedSpecies/ThreatenedSpeciesCard';
import { WaterHoldingCapacity } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/water/WaterCard';
import { isNullish } from '@/utils/booleans';

export enum LandMonitoringSections {
  BIODIVERSITY = 'biodiversity',
  CARBON = 'carbon',
  SOCIAL = 'social',
  WATER = 'water',
  POSIDONIA = 'posidonia',
  CYSTOSEIRA = 'cystoseira',
}

const INDICATOR_PRODUCTS_TO_SECTIONS: Record<
  LandMonitoringSections,
  readonly (typeof INDICATOR)[keyof typeof INDICATOR][]
> = {
  [LandMonitoringSections.BIODIVERSITY]: [
    INDICATOR.ECOSYSTEM_INTEGRITY,
    INDICATOR.LANDSCAPE_SPECIES_PRESENCE,
    INDICATOR.DEFORESTATION,
    INDICATOR.HABITAT_INTACTNESS,
    INDICATOR.EO_VEGETATION_CONDITION,
    INDICATOR.LANDSCAPE_LANDCOVER_LANDUSE,
    INDICATOR.LST_ANALYZE,
    INDICATOR.CLEANWAVE, // For marine EII
  ],
  [LandMonitoringSections.CARBON]: [INDICATOR.CARBON, INDICATOR.ESA_ABOVE_GROUND_CARBON],
  [LandMonitoringSections.WATER]: [INDICATOR.WATER_HIGH_RESOLUTION, INDICATOR.WATER_LOW_RESOLUTION],
  [LandMonitoringSections.SOCIAL]: [INDICATOR.POPULATION_DENSITY],
  [LandMonitoringSections.POSIDONIA]: [INDICATOR.CLEANWAVE],
  [LandMonitoringSections.CYSTOSEIRA]: [INDICATOR.CLEANWAVE],
} as const;

export const LandMonitoringPage = () => {
  return (
    <Stack data-testid='land-monitoring-page'>
      <AccordionWrapper type={AccordionType.MULTIPLE} defaultValue={[]}>
        <LandMonitoringSection
          section={LandMonitoringSections.BIODIVERSITY}
          content={
            <Stack spacing={8}>
              <EcosystemIntegrityIndex />
              <ThreatenedSpecies />
              <LandCoverChangeCard />
              <Deforestation />
              <HabitatIntactness />
              <HabitatIntactnessThreatsBreakdown />
              <HabitatIntactnessThreatsTable />
              <NDVI />
              <LandSurfaceTemperature />
            </Stack>
          }
        />
        <LandMonitoringSection
          section={LandMonitoringSections.CARBON}
          content={
            <Stack spacing={8}>
              <BelowGroundCarbon />
              <AboveGroundCarbon />
            </Stack>
          }
        />
        <LandMonitoringSection section={LandMonitoringSections.WATER} content={<WaterHoldingCapacity />} />
        <LandMonitoringSection section={LandMonitoringSections.SOCIAL} content={<PopulationDensity />} />
        <LandMonitoringSection
          section={LandMonitoringSections.POSIDONIA}
          content={
            <Stack spacing={8}>
              <Stack spacing={12} direction='row'>
                <SurvivalRateCard species={SpeciesEnum.posidonia} />
                <BiodiversityUpliftCard species={SpeciesEnum.posidonia} />
                <ProjectProgressCard species={SpeciesEnum.posidonia} />
              </Stack>
              <SpeciesGradient species={SpeciesEnum.posidonia} />
            </Stack>
          }
        />
        <LandMonitoringSection
          section={LandMonitoringSections.CYSTOSEIRA}
          content={
            <Stack spacing={8}>
              <Stack spacing={12} direction='row'>
                <SurvivalRateCard species={SpeciesEnum.cystoseira} />
                <BiodiversityUpliftCard species={SpeciesEnum.cystoseira} />
                <ProjectProgressCard species={SpeciesEnum.cystoseira} />
              </Stack>
              <SpeciesGradient species={SpeciesEnum.cystoseira} />
            </Stack>
          }
        />
      </AccordionWrapper>
    </Stack>
  );
};

const LandMonitoringSection = ({
  section,
  content,
}: {
  section: LandMonitoringSections;
  content?: ReactNode | null;
}) => {
  const { t } = useTranslation();
  const purchasedIndicatorProducts = useSelectedIndicatorProducts();
  const isOrgSubscribedToIPForSection = purchasedIndicatorProducts.some((ip) =>
    INDICATOR_PRODUCTS_TO_SECTIONS[`${section}`].includes(ip.name as (typeof INDICATOR)[keyof typeof INDICATOR]),
  );

  if (!isOrgSubscribedToIPForSection) {
    return null;
  }

  return (
    <AccordionSection
      title={t(`shared.projects.plot.tabView.landMonitoring.sections.${section}`)}
      value={section}
      styles={{
        trigger: 'bg-neutral-black-4',
        content: 'border border-divider rounded-b-3xl',
      }}
    >
      {isNullish(content) && (
        <span className='typography-body1'>
          {t('shared.projects.plot.tabView.landMonitoring.labels.noDataAvailable')}
        </span>
      )}
      {content}
    </AccordionSection>
  );
};
